import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LocationInput - Google Maps location autocomplete component
 * Uses the backend proxy to avoid CORS issues
 * 
 * Props:
 * - value: current location string
 * - onChange: callback with selected location string
 * - onSelect: optional callback with full location object { description, main_text, secondary_text, place_id }
 * - placeholder: input placeholder text
 * - className: additional CSS classes
 * - disabled: disable input
 */
export default function LocationInput({ 
  value = "", 
  onChange, 
  onSelect,
  placeholder = "Search city or location",
  className = "",
  disabled = false 
}) {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch predictions with debounce
  const fetchPredictions = async (query) => {
    if (!query || query.length < 2) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/places/autocomplete?input=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setPredictions(data.predictions || []);
        setIsOpen(true);
      }
    } catch (err) {
      console.error("Location search failed:", err);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300);
  };

  const handleSelect = (prediction) => {
    const locationString = prediction.description || prediction.main_text;
    setInputValue(locationString);
    setIsOpen(false);
    setPredictions([]);
    
    if (onChange) {
      onChange(locationString);
    }
    if (onSelect) {
      onSelect(prediction);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10"
          data-testid="location-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Predictions Dropdown */}
      {isOpen && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction, idx) => (
            <button
              key={prediction.place_id || idx}
              type="button"
              onClick={() => handleSelect(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start gap-3 border-b last:border-b-0"
            >
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm text-gray-900 truncate">
                  {prediction.main_text}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {prediction.secondary_text}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
